// Resolve a category list item — which may be a plain string (legacy demo data)
// or a FoundationCategory object `{ "@id": "...", "@name": "...", "displayName": "..." }`
// returned by the external-commerce sources (commercetools, salesforce-sfcc) — to
// a stable label for rendering and equality checks. Prefers displayName so the UI
// shows "Earrings" rather than "womens-jewelry-earrings".
export const categoryLabel = (single) => {
    if (single == null) return null;
    if (typeof single === "string") return single;
    return single.displayName || single["@name"] || single["@id"] || single.id || null;
};

// get products
export const getProducts = (products, category, type, limit) => {
    const finalProducts = category ? products.filter((product) => product.category && product.category.filter((single) => categoryLabel(single) === category)[0]) : products;

    if (type && type === "new") {
        const newProducts = finalProducts.filter((single) => single.new);
        return newProducts.slice(0, limit ? limit : newProducts.length);
    }
    if (type && type === "bestSeller") {
        return finalProducts
            .sort((a, b) => {
                return b.saleCount - a.saleCount;
            })
            .slice(0, limit ? limit : finalProducts.length);
    }
    if (type && type === "saleItems") {
        const saleItems = finalProducts.filter((single) => single.discount && single.discount > 0);
        return saleItems.slice(0, limit ? limit : saleItems.length);
    }
    return finalProducts.slice(0, limit ? limit : finalProducts.length);
};

// get product discount price
export const getDiscountPrice = (price, discount) => {
    return discount && discount > 0 ? price - price * (discount / 100) : null;
};

export const getProductPrices = (price, discount, currencyRate) => {
    const discountedPrice = getDiscountPrice(price, discount);
    const finalProductPrice = +(price * currencyRate).toFixed(2);
    const finalDiscountedPrice = +(discountedPrice * currencyRate).toFixed(2);

    return { discountedPrice, finalProductPrice, finalDiscountedPrice };
};

// get product cart quantity
export const getProductCartQty = (cartItems, product, color, size) => {
    let productInCart = cartItems.filter(
        (single) =>
            single.id === product.id &&
            (single.selectedProductColor ? single.selectedProductColor === color : true) &&
            (single.selectedProductSize ? single.selectedProductSize === size : true),
    )[0];
    if (cartItems.length >= 1 && productInCart) {
        if (product.variation) {
            return cartItems.filter(
                (single) => single.id === product.id && single.selectedProductColor === color && single.selectedProductSize === size,
            )[0].quantity;
        } else {
            return cartItems.filter((single) => product.id === single.id)[0].quantity;
        }
    } else {
        return 0;
    }
};

//get products based on category
export const getSortedProducts = (products, sortType, sortValue) => {
    if (products && sortType && sortValue) {
        if (sortType === "category") {
            return products.filter((product) => product.category && product.category.filter((single) => categoryLabel(single) === sortValue)[0]);
        }
        if (sortType === "tag") {
            return products.filter((product) => product.tag.filter((single) => single === sortValue)[0]);
        }
        if (sortType === "color") {
            return products.filter(
                (product) =>
                    product.variation &&
                    product.variation["@nodes"] &&
                    product.variation["@nodes"].filter(
                        (variationName) => product.variation[variationName].color && product.variation[variationName].color === sortValue,
                    )[0],
            );
        }
        if (sortType === "size") {
            return products.filter(
                (product) =>
                    product.variation &&
                    product.variation["@nodes"] &&
                    product.variation["@nodes"].filter(
                        (variationName) =>
                            product.variation[variationName].size &&
                            product.variation[variationName]["@nodes"] &&
                            product.variation[variationName].size["@nodes"].filter(
                                (sizeName) => product.variation[variationName].size[sizeName].name === sortValue,
                            )[0],
                    )[0],
            );
        }
        if (sortType === "filterSort") {
            let sortProducts = [...products];
            if (sortValue === "default") {
                return sortProducts;
            }
            if (sortValue === "priceHighToLow") {
                return sortProducts.sort((a, b) => {
                    return b.price - a.price;
                });
            }
            if (sortValue === "priceLowToHigh") {
                return sortProducts.sort((a, b) => {
                    return a.price - b.price;
                });
            }
        }
    }
    return products;
};

// get unique element
const getUniqueElemArray = (array) => {
    let uniqueElemArray = array.filter(function (v, i, self) {
        return i === self.indexOf(v);
    });
    return uniqueElemArray;
};

// get unique categories — flatten product.category[] across all products and dedupe
// on the resolved label. Tolerates both legacy plain-string categories and the
// object shape produced by FoundationCategory.
export const getUniqueCategories = (products) => {
    let productCategories = [];

    if (!products) return [];

    for (const product of products) {
        if (!product.category) continue;
        for (const single of product.category) {
            const label = categoryLabel(single);
            if (label) productCategories.push(label);
        }
    }

    const uniqueProductCategories = getUniqueElemArray(productCategories);
    return uniqueProductCategories;
};

// get unique tags
export const getUniqueTags = (products) => {
    let productTags = [];
    products &&
        products.map((product) => {
            return (
                product.tag &&
                product.tag.map((single) => {
                    return productTags.push(single);
                })
            );
        });
    const uniqueProductTags = getUniqueElemArray(productTags);
    return uniqueProductTags;
};

// get unique colors
export const getUniqueColors = (products) => {
    let productColors = [];

    if (!products) return [];

    for (const product of products) {
        if (!product.variation || !product.variation["@nodes"]) continue;

        for (const variationName of product.variation["@nodes"]) {
            if (product.variation[variationName].color) productColors.push(product.variation[variationName].color);
        }
    }

    const uniqueProductColors = getUniqueElemArray(productColors);
    return uniqueProductColors;
};

// get unique sizes
export const getProductsUniqueSizes = (products) => {
    let productSizes = [];

    if (!products) return [];

    for (const product of products) {
        if (!product.variation || !product.variation["@nodes"]) continue;

        for (const variationName of product.variation["@nodes"]) {
            if (!product.variation[variationName].size || !product.variation[variationName].size["@nodes"]) continue;

            for (const sizeName of product.variation[variationName].size["@nodes"]) {
                if (product.variation[variationName].size[sizeName].name)
                    productSizes.push(product.variation[variationName].size[sizeName].name);
            }
        }
    }
    const uniqueProductSizes = getUniqueElemArray(productSizes);
    return uniqueProductSizes;
};

// get product unique sizes
export const getUniqueSizes = (product) => {
    let productSizes = [];
    product.variation &&
        product.variation.map((singleVariation) => {
            return (
                singleVariation.size &&
                singleVariation.size.map((singleSize) => {
                    return productSizes.push(singleSize.name);
                })
            );
        });
    const uniqueProductSizes = getUniqueElemArray(productSizes);
    return uniqueProductSizes;
};

export const setActiveSort = (e) => {
    const filterButtons = document.querySelectorAll(".sidebar-widget-list-left button, .sidebar-widget-tag button, .product-filter button");
    filterButtons.forEach((elem) => {
        elem.classList.remove("active");
    });
    e.currentTarget.classList.add("active");
};

export const setActiveLayout = (e) => {
    const gridSwitchBtn = document.querySelectorAll(".shop-tab button");
    gridSwitchBtn.forEach((elem) => {
        elem.classList.remove("active");
    });
    e.currentTarget.classList.add("active");
};

export const toggleShopTopFilter = (e) => {
    const shopTopFilterWrapper = document.querySelector("#product-filter-wrapper");
    shopTopFilterWrapper.classList.toggle("active");
    if (shopTopFilterWrapper.style.height) {
        shopTopFilterWrapper.style.height = null;
    } else {
        shopTopFilterWrapper.style.height = shopTopFilterWrapper.scrollHeight + "px";
    }
    e.currentTarget.classList.toggle("active");
};
